/**
 * BookingCard Block — Specialized card for travel/commerce bookings
 *
 * Shows provider, price (with optional discount), dates, rating,
 * key details, and a deep link CTA.
 *
 * Responsive: stacks on mobile, side-by-side on desktop.
 */
import React from 'react';
import type { BookingCardData } from '../types';
import { ChevronRight } from '../../shared';

// =============================================================================
// STAR RATING HELPER
// =============================================================================

const StarRating: React.FC<{ rating: number; count?: number }> = ({ rating, count }) => {
  const fullStars = Math.floor(rating);
  const hasHalf = rating - fullStars >= 0.25 && rating - fullStars < 0.75;
  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);

  return (
    <div className="flex items-center gap-1.5">
      <div className="flex gap-0.5">
        {Array.from({ length: fullStars }).map((_, i) => (
          <svg key={`f${i}`} className="w-3.5 h-3.5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
        {hasHalf && (
          <svg className="w-3.5 h-3.5 text-amber-400" viewBox="0 0 20 20">
            <defs>
              <linearGradient id="halfStar">
                <stop offset="50%" stopColor="currentColor" />
                <stop offset="50%" stopColor="transparent" />
              </linearGradient>
            </defs>
            <path fill="url(#halfStar)" stroke="currentColor" strokeWidth={0.5} d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        )}
        {Array.from({ length: emptyStars }).map((_, i) => (
          <svg key={`e${i}`} className="w-3.5 h-3.5 text-cyan-400/20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
      <span className="text-xs text-cyan-400/60 font-medium">
        {rating.toFixed(1)}
        {count !== undefined && <span className="text-cyan-400/40"> ({count.toLocaleString()})</span>}
      </span>
    </div>
  );
};

// =============================================================================
// COMPONENT
// =============================================================================

interface BookingCardProps {
  data: BookingCardData;
  onBookAction?: (bookingData: BookingCardData) => void;
}

export const BookingCard: React.FC<BookingCardProps> = ({ data, onBookAction }) => {
  // Guard: bail out if data is missing to prevent destructure crash
  if (!data) return null;

  const {
    provider = '',
    providerLogo,
    title = '',
    price = '',
    originalPrice,
    currency,
    rating,
    ratingCount,
    dates,
    details,
    image,
    bookingUrl,
    ctaLabel,
  } = data;

  const hasDiscount = originalPrice && originalPrice !== price;

  const handleCardClick = onBookAction
    ? (e: React.MouseEvent) => {
        // Avoid double-fire when the CTA button itself is clicked
        if ((e.target as HTMLElement).closest('button, a')) return;
        onBookAction(data);
      }
    : undefined;

  return (
    <div
      role={onBookAction ? 'button' : undefined}
      tabIndex={onBookAction ? 0 : undefined}
      onClick={handleCardClick}
      onKeyDown={onBookAction ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onBookAction(data); } } : undefined}
      className={`relative overflow-hidden rounded-2xl bg-black/50 backdrop-blur-xl border border-cyan-400/25 hover:border-cyan-400/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(34,211,238,0.2)] ${onBookAction ? 'cursor-pointer active:scale-[0.98]' : ''}`}
    >
      {/* Desktop: side-by-side | Mobile: stacked */}
      <div className="flex flex-col md:flex-row">
        {/* Image section */}
        {image && (
          <div className="relative w-full md:w-56 h-44 md:h-auto flex-shrink-0 overflow-hidden">
            <img
              src={image}
              alt={title}
              loading="lazy"
              className="w-full h-full object-cover opacity-80"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/50 hidden md:block" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent md:hidden" />
          </div>
        )}

        {/* Content section */}
        <div className="flex-1 p-4 md:p-5 flex flex-col">
          {/* Provider row */}
          <div className="flex items-center gap-2 mb-2">
            {providerLogo && (
              <img
                src={providerLogo}
                alt={provider}
                className="w-5 h-5 rounded object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            <span className="text-[11px] text-cyan-400/50 font-bold uppercase tracking-wider">
              {provider}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-base md:text-lg font-bold text-cyan-300 mb-2 leading-tight drop-shadow-[0_0_3px_rgba(34,211,238,0.3)]">
            {title}
          </h3>

          {/* Rating */}
          {rating !== undefined && (
            <div className="mb-2">
              <StarRating rating={Number(rating) || 0} count={ratingCount} />
            </div>
          )}

          {/* Dates */}
          {dates && (
            <p className="text-xs text-cyan-400/60 mb-3 flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              {dates}
            </p>
          )}

          {/* Key details */}
          {Array.isArray(details) && details.length > 0 && (
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-4">
              {details.map((d, i) => (
                <div key={i} className="text-xs">
                  <span className="text-cyan-400/50 font-medium">{d.label}: </span>
                  <span className="text-cyan-300/80">{d.value}</span>
                </div>
              ))}
            </div>
          )}

          {/* Price + CTA row */}
          <div className="mt-auto flex items-end justify-between gap-4 pt-3 border-t border-cyan-400/10">
            {/* Price */}
            <div className="flex flex-col">
              {hasDiscount && (
                <span className="text-xs text-cyan-400/40 line-through">
                  {currency || ''}{originalPrice}
                </span>
              )}
              <span className={`text-2xl font-bold drop-shadow-[0_0_5px_rgba(34,211,238,0.5)] ${hasDiscount ? 'text-emerald-400' : 'text-cyan-400'}`}>
                {currency || ''}{price}
              </span>
            </div>

            {/* CTA button — triggers pay flow directly when onBookAction is available */}
            {onBookAction ? (
              <button
                onClick={() => onBookAction(data)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-400 text-black font-bold text-sm shadow-[0_0_20px_rgba(34,211,238,0.4)] hover:bg-cyan-300 transition-all duration-200 active:scale-95 flex-shrink-0"
              >
                {ctaLabel || 'Book Now'}
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <a
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-400 text-black font-bold text-sm shadow-[0_0_20px_rgba(34,211,238,0.4)] hover:bg-cyan-300 transition-all duration-200 active:scale-95 flex-shrink-0"
              >
                {ctaLabel || 'Book Now'}
                <ChevronRight className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingCard;
